<template>
  <div id="listing-card">
    <div class="cards-container">
      <SmallLoader v-if="smallLoading && card === 'listingCard'" />
      <div v-else class="cards-grid">
        <router-link
          v-for="(token, index) in raising"
          :key="index"
          :to="{
            name: 'TokenOverview',
            params: { symbol: token.symbol, id: token._id },
          }"
          class="listing-card"
        >
          <!-- Card Header -->
          <div class="card-header">
            <div class="token-avatar">
              <img 
                :src="token.image" 
                :alt="token.name"
                @error="handleImageError"
              />
            </div>
            <div class="header-badges">
              <span v-if="isNewListing(token)" class="status-badge status-badge--live">
                <span class="status-dot"></span>
                Live
              </span>
              <DealTypeBadge :badge="viewOf(token).badge" size="sm" />
              <ShariaBadge :token="token" size="sm" />
            </div>
          </div>

          <!-- Token Info -->
          <div class="token-details">
            <h3 class="token-name">{{ token.name }}</h3>
            <div class="token-meta">
              <span class="token-symbol">{{ token.symbol }}</span>
              <span class="meta-dot"></span>
              <span class="token-industry">{{ subtitleOf(token) }}</span>
            </div>
          </div>

          <!-- The one figure that matters for this kind of deal (utils/trade-view). -->
          <div v-if="viewOf(token).hero" class="card-hero">
            <span class="hero-value">{{ viewOf(token).hero.value }}</span>
            <span class="hero-unit">{{ viewOf(token).hero.unit }}</span>
          </div>

          <!-- Metals and NGX stocks sell at the market price with no goal: what is left, not a bar. -->
          <div v-if="byAvailability(token)" class="stats-row">
            <div class="stat-block">
              <span class="stat-value">{{ availableOf(token) }}</span>
              <span class="stat-label">Available</span>
            </div>
            <div class="stat-divider"></div>
            <div class="stat-block">
              <span class="stat-value">{{ formatCompactAmount(token.min_trade?.buy || 0, token.currency) }}</span>
              <span class="stat-label">Min.</span>
            </div>
          </div>

          <!-- Stats Row -->
          <div v-else class="stats-row">
            <div class="stat-block">
              <span class="stat-value">{{ formatCompactAmount(raisedOf(token), token.currency) }}</span>
              <span class="stat-label">Raised</span>
            </div>
            <div class="stat-divider"></div>
            <div class="stat-block">
              <span class="stat-value">{{ formatCompactAmount(token.raise_amount || 0, token.currency) }}</span>
              <span class="stat-label">Target</span>
            </div>
            <div class="stat-divider"></div>
            <div class="stat-block">
              <span class="stat-value stat-value--highlight">{{ getProgress(token) }}%</span>
              <span class="stat-label">Funded</span>
            </div>
          </div>

          <!-- Progress Bar -->
          <div v-if="!byAvailability(token)" class="progress-container">
            <div class="progress-track">
              <div class="progress-fill" :style="{ width: getProgress(token) + '%' }"></div>
            </div>
          </div>

          <!-- Footer Action -->
          <div class="card-footer">
            <span class="invest-cta">
              <span class="cta-text">{{ ctaOf(token) }}</span>
              <svg class="cta-icon" viewBox="0 0 16 16" fill="none">
                <path d="M3 8H13M13 8L9 4M13 8L9 12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </span>
          </div>
        </router-link>
      </div>
    </div>
  </div>
</template>

<script>
  import { formatAmountToDollar, getCurrencySymbol, formatCompactAmount } from "@/utils/helpers";
  import { mapActions, mapState } from "vuex";
  import DealTypeBadge from "@/components/general/DealTypeBadge.vue";
  import ShariaBadge from "@/components/general/ShariaBadge.vue";
  import { DealKinds, availableLabel, listingView, raiseFigures, sellsByAvailability } from "@/utils/trade-view";

  export default {
    name: "ListingCard",

    components: { DealTypeBadge, ShariaBadge },

    data() {
      return {
        card: "",
        raising: [],
      };
    },

    computed: {
      ...mapState({
        smallLoading: (state) => state.smallLoading,
        companiesRaising: (state) => state.TokenModule.raisingTokens,
      }),
    },

    mounted() {
      this.getDetails();
    },

    methods: {
      ...mapActions(["setLoading"]),
      ...mapActions("TokenModule", ["getCompaniesRaising"]),
      formatAmountToDollar,
      getCurrencySymbol,
      formatCompactAmount,

      viewOf(token) {
        return listingView(token);
      },

      byAvailability(token) {
        return sellsByAvailability(token);
      },

      availableOf(token) {
        return availableLabel(token);
      },

      // A metal says what a token is and a stock where it trades; anything else, its industry.
      subtitleOf(token) {
        const view = this.viewOf(token);
        if (view.kind === DealKinds.COMMODITY && token.market?.token_is) return `1 token = ${token.market.token_is}`;
        if (view.kind === DealKinds.LISTED) return "Listed on the Nigerian Exchange";
        return token.creator?.industry || "Technology";
      },

      ctaOf(token) {
        const view = this.viewOf(token);
        return [DealKinds.COMMODITY, DealKinds.LISTED].includes(view.kind) ? view.cta : "Invest Now";
      },

      // total_raised counts units, not money: the share of supply sold, from one set of figures.
      getProgress(token) {
        return raiseFigures(token).percent.toFixed(0);
      },

      raisedOf(token) {
        return raiseFigures(token).raised;
      },

      isNewListing(token) {
        if (!token.createdAt) return false;
        const createdDate = new Date(token.createdAt);
        const now = new Date();
        const daysDiff = (now - createdDate) / (1000 * 60 * 60 * 24);
        return daysDiff <= 14;
      },

      handleImageError(e) {
        e.target.src = require("@/assets/img/token-icon.svg");
      },

      async getDetails() {
        this.setLoading(true);
        this.raising = [];
        this.card = "listingCard";
        const payload = { page: 1, limit: 5 };
        await this.getCompaniesRaising(payload);
        this.raising = [...this.companiesRaising].slice(0, 5);
        this.card = "";
        this.setLoading(false);
      },
    },
  };
</script>

<style lang="scss" scoped>
#listing-card {
  .cards-container {
    position: relative;
  }

  .cards-grid {
    display: flex;
    gap: 16px;
    overflow-x: auto;
    padding: 4px 0 12px;
    scroll-snap-type: x mandatory;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
    -ms-overflow-style: none;

    &::-webkit-scrollbar {
      display: none;
    }
  }

  .listing-card {
    display: flex;
    flex-direction: column;
    flex: 0 0 280px;
    min-width: 280px;
    padding: 20px;
    background: #FFFFFF;
    border: 1px solid #E5E7EB;
    border-radius: 12px;
    text-decoration: none;
    scroll-snap-align: start;
    transition: all 0.2s ease;

    &:hover {
      border-color: #3B82F6;
      box-shadow: 0 4px 20px rgba(59, 130, 246, 0.12);

      .invest-cta {
        background: #3B82F6;
        color: #FFFFFF;
      }

      .cta-icon {
        transform: translateX(4px);
      }
    }
  }

  // Card Header
  .card-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 16px;
  }

  .token-avatar {
    width: 48px;
    height: 48px;
    border-radius: 10px;
    overflow: hidden;
    background: #F9FAFB;
    border: 1px solid #E5E7EB;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  .header-badges {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 6px;
  }

  .status-badge {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 4px 10px;
    border-radius: 6px;
    font-size: 11px;
    font-weight: 500;

    &--live {
      background: #ECFDF5;
      color: #059669;
    }
  }

  .status-dot {
    width: 6px;
    height: 6px;
    background: #10B981;
    border-radius: 50%;
    animation: pulse 2s infinite;
  }

  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.5; }
  }

  .card-hero {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    gap: 2px 6px;
    margin: -8px 0 16px;
  }

  .hero-value {
    font-size: 18px;
    font-weight: 500;
    color: #111827;
  }

  .hero-unit {
    font-size: 12px;
    color: #6B7280;
  }

  // Token Details
  .token-details {
    margin-bottom: 20px;
  }

  .token-name {
    font-size: 16px;
    font-weight: 500;
    color: #111827;
    margin: 0 0 6px 0;
    line-height: 1.3;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .token-meta {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    color: #6B7280;
  }

  .token-symbol {
    font-weight: 500;
    color: #3B82F6;
  }

  .meta-dot {
    width: 3px;
    height: 3px;
    background: #D1D5DB;
    border-radius: 50%;
  }

  .token-industry {
    font-weight: 400;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  // Stats Row
  .stats-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 0;
    border-top: 1px solid #F3F4F6;
    border-bottom: 1px solid #F3F4F6;
    margin-bottom: 16px;
  }

  .stat-block {
    display: flex;
    flex-direction: column;
    align-items: center;
    flex: 1;
  }

  .stat-value {
    font-size: 15px;
    font-weight: 500;
    color: #111827;
    margin-bottom: 2px;

    &--highlight {
      color: #3B82F6;
    }
  }

  .stat-label {
    font-size: 11px;
    font-weight: 400;
    color: #9CA3AF;
    text-transform: uppercase;
    letter-spacing: 0.3px;
  }

  .stat-divider {
    width: 1px;
    height: 32px;
    background: #E5E7EB;
  }

  // Progress
  .progress-container {
    margin-bottom: 16px;
  }

  .progress-track {
    height: 4px;
    background: #E5E7EB;
    border-radius: 2px;
    overflow: hidden;
  }

  .progress-fill {
    height: 100%;
    background: #3B82F6;
    border-radius: 2px;
    transition: width 0.4s ease;
  }

  // Footer
  .card-footer {
    margin-top: auto;
  }

  .invest-cta {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    width: 100%;
    padding: 12px 16px;
    background: #F3F4F6;
    border-radius: 8px;
    transition: all 0.2s ease;
  }

  .cta-text {
    font-size: 13px;
    font-weight: 500;
    color: #374151;
  }

  .listing-card:hover .cta-text {
    color: #FFFFFF;
  }

  .cta-icon {
    width: 16px;
    height: 16px;
    color: #6B7280;
    transition: all 0.2s ease;
  }

  .listing-card:hover .cta-icon {
    color: #FFFFFF;
  }
}
</style>
